import React, { useContext } from 'react';
import { useAuth } from './AuthContext';
import {
  MyAccount,
  Dashboard,
  PatientDetails,
  TeamInfoPage,
  SearchPage,
  ReportingPage,
  SettingsPage,
} from '../ai2';
import { NoAccess, NoAccessPatient } from '../auth/pages/NoAccess';
import PermissionMissing from '../auth/pages/PermissionMissing';
import { Ai2Layout } from '../ai2/layout/Ai2Layout';
import { ClinicSettings } from '../ai2/pages/ClinicSettings';
import { Legal } from '../ai2/pages/Legal/Legal';
import { ErrorPage } from '../auth/pages/Error';
import { ActivityFunding } from '../ai2/pages/ActivityFunding';
import AuthenticationCard from '../ai2/components/AuthenticationCard';
import IndexRoute from '../routes/IndexRoute';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from '../routes/ProtectedRoute';
import DataAnnotationPage from '../ai2/pages/DataAnnotation/DataAnnotationPage';
import WithingsCallback from '../ai2/pages/ExternalConnections/WithingsCallback';
import ChatHome from '../ai2/pages/ChatHome/ChatHome';

const getRoutes = (
  isLoading: boolean,
  initialise: () => Promise<void>,
  isAuth: boolean,
) => [
  { path: '/error', element: <ErrorPage pageKey={'error'} /> },
  { path: '/terms', element: <Legal pageKey={'terms'} /> },
  { path: '/privacy', element: <Legal pageKey={'privacy'} /> },
  { path: '/login', element: <AuthenticationCard /> },
  { path: '/noaccess', element: <NoAccess /> },
  {
    path: '/permission-missing',
    element: (
      <Ai2Layout>
        <PermissionMissing />
      </Ai2Layout>
    ),
  },
  { path: '/no_access_patient', element: <NoAccessPatient /> },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <IndexRoute isAuth={isAuth} />
      </ProtectedRoute>
    ),
  },
  {
    path: '/myaccount',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <MyAccount />,
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/clinicsettings',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <ClinicSettings />
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/patientDetails',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <PatientDetails />
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/chat',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <ChatHome />
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/activityFunding',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <ActivityFunding />
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <SettingsPage />
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/team-info',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <TeamInfoPage />
      </ProtectedRoute>
    ),
    isProtected: true,
  },
  {
    path: '/data-annotation',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <DataAnnotationPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/data-annotation-resolving',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <DataAnnotationPage discrepency />
      </ProtectedRoute>
    ),
  },
  {
    path: '/search',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <SearchPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/reporting',
    element: (
      <ProtectedRoute
        isAuthenticated={isAuth}
        isLoading={isLoading}
        initialise={initialise}
      >
        <ReportingPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/external-connections/withings/callback',
    element: <WithingsCallback />,
  },
  { path: '/*', element: <Navigate to="/" /> },
];

type Route = { path: string; element: React.ReactNode; isProtected?: boolean };

interface RoutesType {
  routes: Route[];
  urlExists: (url: string) => boolean;
}

const RoutesContext = React.createContext<RoutesType>({
  routes: [],
  urlExists: () => false,
});

const useRoutes = () => useContext(RoutesContext);

const RoutesProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuth, isLoading, initialise } = useAuth();

  const routes: Route[] = getRoutes(isLoading, initialise, isAuth);

  const urlExists = (url: string) => {
    return routes.some((route) => route.path === url);
  };
  return (
    <RoutesContext.Provider value={{ routes, urlExists }}>
      {children}
    </RoutesContext.Provider>
  );
};

export default RoutesProvider;
export { useRoutes };
